import axios from 'axios'

export const LOCKED_CODES: Record<string, string> = {
  INVALID_SOLANA_ADDRESS: 'Enter a valid Solana wallet address',
  INSUFFICIENT_BALANCE: 'Amount exceeds available balance',
  UNKNOWN_METHOD: 'Payout method is no longer available',
  AMOUNT_NOT_POSITIVE: 'Enter a valid amount',
  INVALID_EMAIL: 'Enter a valid email address',
  // Tremendous withdraw-path codes. Plain English like every entry above — deliberately NOT lingui
  // descriptors [P5-90], so they are not extracted and not translated.
  // Server-code path only: the client-side cap check in WithdrawModal's validateAmount renders its
  // own number-bearing string instead, because this map cannot interpolate the $2,000 figure.
  ABOVE_MAX_AMOUNT: 'Amount exceeds the maximum for this payout method',
  // Field-less. Permanent: this account is not eligible for Tremendous payouts.
  TREMENDOUS_RECIPIENT_BLOCKED: 'Tremendous payouts are unavailable for this account',
  // Field-less. Transient by design (IV-27): a temporary eligibility-read failure must not read like
  // the permanent block above.
  TREMENDOUS_ELIGIBILITY_UNAVAILABLE: "Couldn't check payout eligibility — try again shortly",
  // Field-less. Transient: the access refusal lifts when Tremendous opens to everyone, so it must
  // not read like the account block above.
  TREMENDOUS_NOT_AVAILABLE: 'Tremendous payouts are not available right now. Please choose another method.'
}

export type PaymentsValidationDetail = {
  // Absent when the error is not attributable to an input — e.g. any method with `fields: []`
  // structurally cannot carry a field-keyed error.
  field?: string
  code: string
  message: string
}

export type PaymentsValidationError = {
  fieldErrors: Record<string, string>
  formError: string | null
  // The machine code of the first field-less detail, so a caller can render its own translated copy.
  formErrorCode: string | null
}

export function parsePaymentsValidationError(err: unknown): PaymentsValidationError | null {
  if (!axios.isAxiosError(err)) {
    return null
  }

  const body = err.response?.data

  if (typeof body !== 'object' || body === null) {
    return null
  }

  const errorField = (body as { error?: unknown }).error
  if (typeof errorField !== 'object' || errorField === null) {
    return null
  }

  const code = (errorField as { code?: unknown }).code
  if (code !== 'VALIDATION') {
    return null
  }

  const message = (errorField as { message?: unknown }).message
  const messageStr = typeof message === 'string' ? message : ''

  const detailsRaw = (errorField as { details?: unknown }).details
  const details: PaymentsValidationDetail[] = Array.isArray(detailsRaw)
    ? (detailsRaw as PaymentsValidationDetail[])
    : []

  const fieldErrors: Record<string, string> = {}
  let anyDetailHadField = false
  let fieldlessCode: string | null = null
  let fieldlessMessage: string | null = null

  for (const detail of details) {
    if (typeof detail !== 'object' || detail === null) {
      continue
    }
    const field = (detail as { field?: unknown }).field
    const dCode = (detail as { code?: unknown }).code
    const dMessage = (detail as { message?: unknown }).message
    const codeStr = typeof dCode === 'string' ? dCode : ''
    const wireMessage = typeof dMessage === 'string' ? dMessage : ''
    if (typeof field === 'string' && field.length > 0) {
      anyDetailHadField = true
      fieldErrors[field] = LOCKED_CODES[codeStr] ?? wireMessage
      continue
    }
    // A field-less detail still carries a machine code; surface it through LOCKED_CODES rather
    // than dropping it and falling back to the untranslated top-level message. First one wins.
    if (fieldlessCode === null && codeStr.length > 0) {
      fieldlessCode = codeStr
      fieldlessMessage = LOCKED_CODES[codeStr] ?? wireMessage
    }
  }

  const formError = fieldlessMessage ?? (anyDetailHadField ? null : messageStr)

  return { fieldErrors, formError, formErrorCode: fieldlessCode }
}
